import * as stylex from "@stylexjs/stylex";
import { TrajectoriesChart } from "charts/TrajectoriesChart";
import { useReadScrolly } from "components/scrolly";
import { useConfig } from "config";
import { format } from "d3-format";
import React, { useMemo } from "react";
import { defineMessage, useIntl } from "react-intl";
import { withFigureIO } from "../components/figure";
import { metadata } from "../data/data_learning_trajectories_fig_TRAJECTORIES_CURRENT";
import { datasets } from "../data/data_learning_trajectories_fig_TRAJECTORIES_CURRENT_datasets";
import { countryIdIso, forceUnwrapCountryOrRegion, getCountryMeta, getRegionOrCountryName, goalRule, RiseDataDecoder, regionIdIso, splitCountriesAndRegions, xAxisSubtitleLookup, xAxisTitleLookup, yAxisTitleLookup } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { E, io, O, pipe } from "../prelude";
export * from "../data/data_learning_trajectories_fig_TRAJECTORIES_CURRENT";
// Internationalization messages
const steepTrajectoryLabel = defineMessage({
    id: "fig.learning.trajectories.TRAJECTORIES_CURRENT.steepTrajectory",
    defaultMessage: "Steep trajectory"
});
const flatTrajectoryLabel = defineMessage({
    id: "fig.learning.trajectories.TRAJECTORIES_CURRENT.flatTrajectory",
    defaultMessage: "Flat trajectory"
});
const lmicAverageLabel = defineMessage({
    id: "fig.learning.trajectories.TRAJECTORIES_CURRENT.lmicAverage",
    defaultMessage: "Average"
});
// Scrollytelling steps
const Steps = io.union([
    io.literal("HIGHLIGHT_VIETNAM"),
    io.literal("LMIC_GRADE_3"),
    io.literal("LMIC_GRADE_6"),
    io.literal("DIFFERENCE_TRAJECTORIES"),
    io.literal("default")
]);
// Line styles
const backgroundSeriesStyle = {
    stroke: M.grayscalePalette[4],
    strokeWidth: 1.5
};
const highlightLMICProps = {
    lineStyle: (series)=>{
        if (series === "lmic") {
            return {
                stroke: M.q07Palette[6],
                strokeDasharray: "0 6",
                strokeWidth: 3
            };
        }
        return backgroundSeriesStyle;
    },
    backgroundSeriesFilter: (series)=>series !== "lmic"
};
export const Data = RiseDataDecoder([
    "value",
    "country",
    "year"
], {});
export const Chart = ({ data, renderMode })=>{
    const screenSDown = !useMedia(breakpoints.s);
    const { activeSection } = useReadScrolly();
    const env = useConfig();
    const intl = useIntl();
    const activeSectionId = pipe(Steps.decode(O.toNullable(activeSection)), E.getOrElse(()=>Steps.encode("default")));
    const { countries: countryList, regions: regionList } = splitCountriesAndRegions({
        input: data.map(({ country })=>({
                country
            })),
        regions: env.regions,
        countries: env.countries
    });
    const lowerAndLowerMiddleIncomeCountries = countryList.filter((country)=>pipe(getCountryMeta(env.countries, countryIdIso.wrap(country.value)), O.fold(()=>false, (c)=>c.income && [
                "Low income",
                "Lower middle income"
            ].includes(regionIdIso.unwrap(c.income)))));
    const countries = [
        ...new Set([
            ...lowerAndLowerMiddleIncomeCountries,
            ...regionList
        ].map((d)=>d.value))
    ].sort().sort((d)=>d === "lmic" ? -1 : 1);
    const chartData = React.useMemo(()=>data.filter((d)=>d.year >= 1 && d.year <= 6 && countries.find((country)=>forceUnwrapCountryOrRegion(d.country) === country)).sort((a, b)=>countries.indexOf(forceUnwrapCountryOrRegion(b.country)) - countries.indexOf(forceUnwrapCountryOrRegion(a.country))), [
        data,
        countries
    ]);
    const chartPropsAtScrollSteps = useMemo(()=>({
            HIGHLIGHT_VIETNAM: {
                lineStyle: (series)=>{
                    if (series === "VNM") {
                        return {
                            stroke: M.q08Palette[5],
                            strokeWidth: 3
                        };
                    }
                    return backgroundSeriesStyle;
                },
                lockActivePoint: chartData.find((d)=>forceUnwrapCountryOrRegion(d.country) === "VNM" && d.year === 3),
                backgroundSeriesFilter: (series)=>series !== "VNM"
            },
            LMIC_GRADE_3: {
                lockActivePoint: chartData.find((d)=>forceUnwrapCountryOrRegion(d.country) === "lmic" && d.year === 3),
                filterSeriesValues: (d)=>forceUnwrapCountryOrRegion(d.country) === "lmic" ? d.year <= 3 : true,
                ...highlightLMICProps
            },
            LMIC_GRADE_6: {
                lockActivePoint: chartData.find((d)=>forceUnwrapCountryOrRegion(d.country) === "lmic" && d.year === 6),
                ...highlightLMICProps
            },
            DIFFERENCE_TRAJECTORIES: {
                lineStyle: (series)=>{
                    if (series === "VNM") {
                        return {
                            stroke: M.q08Palette[5],
                            strokeWidth: 3
                        };
                    }
                    if (series === "COD") {
                        return {
                            stroke: M.q03Palette[7],
                            strokeWidth: 3
                        };
                    }
                    return backgroundSeriesStyle;
                },
                lockActivePoint: chartData.find((d)=>forceUnwrapCountryOrRegion(d.country) === "VNM" && d.year === 3),
                backgroundSeriesFilter: (series)=>![
                        "VNM",
                        "COD"
                    ].includes(series.toString()),
                formatTooltipLabel: (d)=>{
                    if (forceUnwrapCountryOrRegion(d.country) === "VNM") return intl.formatMessage(steepTrajectoryLabel);
                    if (forceUnwrapCountryOrRegion(d.country) === "COD") return intl.formatMessage(flatTrajectoryLabel);
                    return d.country;
                }
            },
            default: highlightLMICProps
        }), [
        chartData,
        intl
    ]);
    const chartProps = renderMode === "static" ? {
        lockActivePoint: chartData.find((d)=>forceUnwrapCountryOrRegion(d.country) === "lmic" && d.year === 6),
        flipTooltip: true,
        ...highlightLMICProps
    } : chartPropsAtScrollSteps[activeSectionId];
    return <div className="x1d7yc3v">
      <TrajectoriesChart height={M.chartHeight.m} dataset={chartData} seriesAccessor={(d)=>forceUnwrapCountryOrRegion(d.country)} xAccessor={(d)=>d.year} yAccessor={(d)=>d.value} yAxisTitle={intl.formatMessage(yAxisTitleLookup["LIT"])} xAxisTitle={intl.formatMessage(xAxisTitleLookup["GRADE"])} xAxisSubtitle={xAxisSubtitleLookup["GRADE"] ? intl.formatMessage(xAxisSubtitleLookup["GRADE"]) : undefined} formatTooltipLabel={(d)=>forceUnwrapCountryOrRegion(d.country) === "lmic" ? intl.formatMessage(lmicAverageLabel) : getRegionOrCountryName({
            input: {
                country: d.country
            },
            regions: env.regions,
            countries: env.countries
        })} formatTooltipValue={(d)=>d.value === null ? intl.formatMessage(defineMessage({
            id: "fig.label.data.not.available",
            defaultMessage: "Not Available"
        })) : format(".0%")(d.value)} backgroundSeriesFilter={(series)=>series !== "lmic"} rules={renderMode === "embed" ? [] : [
        goalRule(intl, "GRADE", "LIT")
    ]} flipTooltip={screenSDown} {...chartProps}/>
    </div>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_CURRENT.json", import.meta.url).href,
    csv: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_CURRENT.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_CURRENT.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "main",
    datasets
});
